import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Globe, ShoppingBag, Workflow, Zap, TrendingUp, Wrench, Server, Plug, Package,
    ArrowRight, ExternalLink, CheckCircle2, Send, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ICONS = {
    globe: Globe,
    "shopping-bag": ShoppingBag,
    workflow: Workflow,
    zap: Zap,
    "trending-up": TrendingUp,
    wrench: Wrench,
    server: Server,
    plug: Plug,
    package: Package,
};

const PROJECT_TYPES = [
    { value: "website", label: "Website institucional" },
    { value: "ecommerce", label: "Loja online" },
    { value: "platform", label: "Plataforma / SaaS" },
    { value: "automation", label: "Automatização de processos" },
    { value: "integration", label: "Integração API" },
    { value: "other", label: "Outro" },
];

const BUDGETS = ["< 1.000 €", "1.000 - 3.000 €", "3.000 - 8.000 €", "8.000 - 20.000 €", "> 20.000 €", "Ainda não sei"];

const TYPE_BADGE = {
    website: "Website",
    ecommerce: "E-commerce",
    platform: "Plataforma",
    saas: "SaaS",
};


export default function Solucoes() {
    const [services, setServices] = useState([]);
    const [portfolio, setPortfolio] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        name: "", email: "", phone: "", company: "",
        project_type: "", budget: "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/services"),
            api.get("/portfolio"),
        ]).then(([s, p]) => {
            setServices(s.data);
            setPortfolio(p.data);
        }).finally(() => setLoading(false));
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Preencha nome, email e mensagem");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact-request", form);
            toast.success("Pedido enviado! Entraremos em contacto em breve.");
            setForm({ name: "", email: "", phone: "", company: "", project_type: "", budget: "", message: "" });
        } catch {
            toast.error("Erro a enviar pedido. Tente novamente.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="solucoes-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b-2 border-ink">
                <div className="grid-bg absolute inset-0 pointer-events-none" />
                <div className="container-edge py-20 md:py-28 relative">
                    <div className="max-w-4xl">
                        <div className="text-sm md:text-base font-mono uppercase tracking-[0.28em] text-magenta mb-8 inline-flex items-center gap-2">
                            <Sparkles size={14} /> Soluções Digitais SOSTONERS
                        </div>
                        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tight leading-[1.02]">
                            Criamos a sua
                            <br />
                            <span className="text-magenta font-medium">presença online.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-base md:text-lg text-ink/70 leading-relaxed">
                            Websites institucionais, lojas online, plataformas automáticas e integrações
                            sob medida. Reduzimos erros humanos, optimizamos processos e damos-lhe
                            ferramentas modernas para escalar.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <a href="#contacto" className="btn-primary" data-testid="hero-cta-contact">
                                Pedir Orçamento <ArrowRight size={18} />
                            </a>
                            <a href="#portfolio" className="btn-secondary">
                                Ver Portfolio
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVIÇOS */}
            <section className="container-edge py-20" id="servicos">
                <div className="mb-12">
                    <div className="label-mono text-magenta mb-3">O que fazemos</div>
                    <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
                        Serviços <span className="font-medium">completos.</span>
                    </h2>
                </div>
                {loading ? (
                    <div className="font-mono text-sm">A carregar serviços…</div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-ink">
                        {services.map((s, i) => {
                            const Icon = ICONS[s.icon] || Package;
                            const isLastCol = (i + 1) % 4 === 0;
                            const isLastRow = i >= services.length - (services.length % 4 || 4);
                            return (
                                <div
                                    key={s.id}
                                    className={`p-6 bg-white hover:bg-paper transition-colors ${!isLastCol ? "border-r-2 border-ink" : ""} ${!isLastRow ? "border-b-2 border-ink" : ""}`}
                                    data-testid={`service-${s.id}`}
                                >
                                    <div className="w-12 h-12 border-2 border-ink flex items-center justify-center bg-magenta text-white mb-4">
                                        <Icon size={22} />
                                    </div>
                                    <h3 className="font-display font-medium text-xl tracking-tight mb-2">{s.title}</h3>
                                    <p className="text-sm text-ink/70 leading-relaxed mb-4">{s.description}</p>
                                    {s.features?.length > 0 && (
                                        <ul className="space-y-1.5">
                                            {s.features.slice(0, 5).map((f, j) => (
                                                <li key={j} className="text-xs font-mono text-ink/80 flex items-start gap-2">
                                                    <CheckCircle2 size={12} className="text-magenta mt-0.5 shrink-0" /> {f}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* PORTFOLIO */}
            <section className="bg-ink text-white border-y-2 border-ink" id="portfolio">
                <div className="container-edge py-20">
                    <div className="mb-12">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-magenta mb-3">Portfolio</div>
                        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight">
                            Projectos <span className="text-magenta font-medium">em produção.</span>
                        </h2>
                    </div>
                    <div className="grid lg:grid-cols-3 gap-6">
                        {portfolio.map((p) => (
                            <a
                                key={p.id}
                                href={p.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group bg-white text-ink border-2 border-magenta hover:shadow-[8px_8px_0_0_#E6007E] transition-all hover:-translate-x-1 hover:-translate-y-1 flex flex-col"
                                data-testid={`portfolio-${p.id}`}
                            >
                                {p.screenshot_url ? (
                                    <div className="aspect-[16/10] overflow-hidden border-b-2 border-ink bg-paper">
                                        <img src={p.screenshot_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                    </div>
                                ) : (
                                    <div className="aspect-[16/10] bg-paper border-b-2 border-ink flex items-center justify-center">
                                        <div className="font-display font-light text-3xl text-ink/30">{p.title}</div>
                                    </div>
                                )}
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.2em] bg-magenta text-white px-2 py-1">
                                            {TYPE_BADGE[p.type] || p.type}
                                        </span>
                                    </div>
                                    <h3 className="font-display font-medium text-2xl tracking-tight mb-2">{p.title}</h3>
                                    <p className="text-sm text-ink/70 leading-relaxed flex-1">
                                        {p.short_description || p.description}
                                    </p>
                                    {p.tags?.length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {p.tags.map((t) => (
                                                <span key={t} className="text-[10px] font-mono uppercase tracking-wider border border-ink/30 px-2 py-0.5">{t}</span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-4 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-magenta">
                                        Visitar site <ExternalLink size={12} />
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACTO / ORÇAMENTO */}
            <section className="container-edge py-20" id="contacto">
                <div className="grid lg:grid-cols-[1fr_1fr] gap-12">
                    <div>
                        <div className="label-mono text-magenta mb-3">Pedido de orçamento</div>
                        <h2 className="font-display font-light text-4xl md:text-5xl tracking-tight mb-6">
                            Vamos construir <span className="font-medium">algo juntos?</span>
                        </h2>
                        <p className="text-ink/70 leading-relaxed mb-6">
                            Conte-nos a sua ideia ou desafio. Respondemos em menos de 24h úteis com
                            uma proposta concreta, calendário e estimativa de investimento.
                        </p>
                        <div className="space-y-4 text-sm">
                            <div className="border-2 border-ink bg-paper p-4">
                                <div className="font-bold uppercase tracking-wider text-xs mb-1">Resposta rápida</div>
                                <div className="text-ink/70">Garantimos primeira resposta em menos de 24 horas úteis.</div>
                            </div>
                            <div className="border-2 border-ink bg-paper p-4">
                                <div className="font-bold uppercase tracking-wider text-xs mb-1">Orçamento gratuito</div>
                                <div className="text-ink/70">A análise e proposta inicial são totalmente gratuitas e sem compromisso.</div>
                            </div>
                            <div className="border-2 border-ink bg-paper p-4">
                                <div className="font-bold uppercase tracking-wider text-xs mb-1">Acompanhamento</div>
                                <div className="text-ink/70">Reuniões semanais e demos contínuas até ao lançamento.</div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="border-2 border-ink bg-white p-8" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <label className="block">
                                <div className="label-mono mb-2">Nome *</div>
                                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-edge" required data-testid="form-name" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Email *</div>
                                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-edge" required data-testid="form-email" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Telefone</div>
                                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-edge" data-testid="form-phone" />
                            </label>
                            <label className="block">
                                <div className="label-mono mb-2">Empresa</div>
                                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-edge" data-testid="form-company" />
                            </label>
                            <label className="block sm:col-span-2">
                                <div className="label-mono mb-2">Tipo de projecto</div>
                                <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="input-edge" data-testid="form-project-type">
                                    <option value="">— Seleccione —</option>
                                    {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </select>
                            </label>
                            <label className="block sm:col-span-2">
                                <div className="label-mono mb-2">Orçamento estimado</div>
                                <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input-edge" data-testid="form-budget">
                                    <option value="">— Seleccione —</option>
                                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                                </select>
                            </label>
                            <label className="block sm:col-span-2">
                                <div className="label-mono mb-2">Conte-nos o que precisa *</div>
                                <textarea
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="input-edge min-h-[140px]"
                                    placeholder="Descreva o seu projecto, objectivos, prazos…"
                                    required
                                    data-testid="form-message"
                                />
                            </label>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full mt-6 justify-center"
                            data-testid="form-submit"
                        >
                            <Send size={16} /> {submitting ? "A enviar…" : "Enviar pedido"}
                        </button>
                        <p className="text-[11px] font-mono text-ink/60 mt-3">
                            Ao enviar concorda com a nossa <Link to="/legal/privacidade" className="underline hover:text-magenta">política de privacidade</Link>.
                        </p>
                    </form>
                </div>
            </section>
        </div>
    );
}
